import { useState, useEffect } from 'react';

/**
 * Custom Hook to detect network status (online/offline)
 * Returns true if online, false if offline
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Handler for when network goes online
    const handleOnline = () => {
      console.log('🌐 Network: Connected');
      setIsOnline(true);
    };

    // Handler for when network goes offline
    const handleOffline = () => {
      console.log('🔴 Network: Disconnected');
      setIsOnline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

/**
 * Enhanced network status with additional checks
 */
export const useEnhancedNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSlowConnection, setIsSlowConnection] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Network: Connected');
      setIsOnline(true);
      setIsSlowConnection(false);
    };

    const handleOffline = () => {
      console.log('🔴 Network: Disconnected');
      setIsOnline(false);
    };

    // Check connection speed if available
    const checkConnectionSpeed = () => {
      const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
      
      if (connection) {
        const effectiveType = connection.effectiveType;
        // Consider 2g and slow-2g as slow connection
        setIsSlowConnection(effectiveType === '2g' || effectiveType === 'slow-2g');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check connection speed initially and on change
    checkConnectionSpeed();
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    if (connection) {
      connection.addEventListener('change', checkConnectionSpeed);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection) {
        connection.removeEventListener('change', checkConnectionSpeed);
      }
    };
  }, []);

  return { isOnline, isSlowConnection };
};

